import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Users, Award, User, GraduationCap, PenTool, FileText, CheckCircle, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import ZenButton from '../components/ZenButton';
import NotificationWidget from '../components/NotificationWidget';
import AutonomieActionCard from '../components/AutonomieActionCard';
import StagiaireAccordionCardTuteur from '../components/tuteur/StagiaireAccordionCardTuteur';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { openPdfBlob } from '../utils/pdfOpener';
import { TuileLivretBase } from '../components/livret-base/TuileLivretBase';

const DashboardTuteur = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [profile, setProfile] = useState(null);
  const [convStageList, setConvStageList] = useState([]);
  const [grilleStructure, setGrilleStructure] = useState(null);

  // Convention signature dialog
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [selectedConv, setSelectedConv] = useState(null);
  const [useProfileSig, setUseProfileSig] = useState(false);
  const signRef = useRef(null);

  // Diplome bravo
  const [showDiplomeBravo, setShowDiplomeBravo] = useState(false);
  const [diplomeStagiaireNom, setDiplomeStagiaireNom] = useState('');

  useEffect(() => {
    loadStagiaires();
    loadProfile();
    loadGrilleStructure();
  }, []);

  useEffect(() => {
    if (stagiaires.length > 0) checkDiplomeStagiaires();
  }, [stagiaires]);

  const checkDiplomeStagiaires = async () => {
    const dismissed = localStorage.getItem(`diplome_bravo_tuteur_${user.user_id}`);
    if (dismissed) return;
    for (const s of stagiaires) {
      try {
        const res = await axios.get(`/stagiaires/${s.user_id}/competences-prerequis`);
        if (res.data.diplome_obtenu) {
          setDiplomeStagiaireNom(`${s.user_info?.prenom || ''} ${s.user_info?.nom || ''}`);
          setShowDiplomeBravo(true);
          break;
        }
      } catch {}
    }
  };

  const dismissDiplomeBravo = () => {
    setShowDiplomeBravo(false);
    sessionStorage.setItem(`diplome_bravo_tuteur_${user.user_id}`, 'true');
    localStorage.setItem(`diplome_bravo_tuteur_${user.user_id}`, 'true');
  };

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data);
      const convs = [];
      for (const s of response.data) {
        try {
          const convRes = await axios.get(`/convention-stage/${s.user_id}`);
          if (convRes.data && convRes.data.statut) {
            convs.push({ ...convRes.data, stagiaire_id: s.user_id, stagiaire_nom: s.user_info?.nom || '', stagiaire_prenom: s.user_info?.prenom || '' });
          }
        } catch {}
      }
      setConvStageList(convs);
    } catch (error) {
    }
  };

  const loadProfile = async () => {
    try {
      const response = await axios.get(`/tuteurs/${user.user_id}`);
      setProfile(response.data.profile);
    } catch (error) {
    }
  };

  const loadGrilleStructure = async () => {
    try {
      const res = await axios.get('/grille-evaluation/structure');
      setGrilleStructure(res.data);
    } catch {}
  };

  const handleSignConvStage = async () => {
    let signature;
    if (useProfileSig && profile?.signature) {
      signature = profile.signature;
    } else {
      if (!signRef.current || signRef.current.isEmpty()) {
        toast.error('Veuillez dessiner votre signature');
        return;
      }
      signature = signRef.current.toDataURL();
    }
    try {
      await axios.post(`/convention-stage/${selectedConv.stagiaire_id}/signer`, { signature });
      toast.success('Convention de Mise en Situation Pédagogique signée !');
      setShowSignDialog(false);
      setSelectedConv(null);
      setUseProfileSig(false);
      loadStagiaires();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const handleViewConv = async (stagiaireId, stagiaireNom = 'stagiaire') => {
    try {
      const res = await axios.get(`/convention-stage/${stagiaireId}/pdf`, { responseType: 'blob' });
      openPdfBlob(res.data, `Convention_${stagiaireNom}.pdf`, 'view');
    } catch {
      toast.error('Impossible de charger le PDF');
    }
  };

  const handleDownloadConv = async (stagiaireId, stagiaireNom = 'stagiaire') => {
    try {
      const res = await axios.get(`/convention-stage/${stagiaireId}/pdf`, { responseType: 'blob' });
      openPdfBlob(res.data, `Convention_${stagiaireNom}.pdf`, 'download');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleOpenSignDialog = (conv) => {
    setSelectedConv(conv);
    setShowSignDialog(true);
  };

  return (
    <div className="space-y-8" data-testid="dashboard-tuteur">
      {/* BANNIERE Conventions en attente de signature */}
      {convStageList.filter(c => c.convention && (c.convention.statut === 'en_attente_signatures' || c.convention.statut === 'partiellement_signe') && !c.convention.signature_tuteur).length > 0 && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-5 text-white shadow-lg" data-testid="banner-convention-tuteur">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-full p-3">
              <PenTool className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold">Convention(s) à signer</h3>
              <p className="text-white/90 text-sm">
                {convStageList.filter(c => c.convention && (c.convention.statut === 'en_attente_signatures' || c.convention.statut === 'partiellement_signe') && !c.convention.signature_tuteur).map(c => `${c.stagiaire_prenom} ${c.stagiaire_nom}`).join(', ')} — Convention de Mise en Situation Pédagogique en attente de votre signature.
              </p>
            </div>
          </div>
        </div>
      )}

      <TuileLivretBase user={user} />

      {/* Header Bandeau */}
      <div className="bg-gradient-to-r from-[#DC2626] to-[#991B1B] rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-shrink-0">
            <div className="w-24 h-32 bg-white/20 border-4 border-white/40 rounded-lg overflow-hidden">
              {profile?.photo ? (
                <img src={profile.photo} alt="Photo" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="h-12 w-12 text-white/60" />
                </div>
              )}
            </div>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{user.prenom} {user.nom}</h1>
            <p className="text-white/80 text-sm mb-3">Tuteur</p>
            <div className="bg-white/10 rounded-lg px-4 py-3">
              <p className="font-semibold flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                {profile?.diplome || 'Diplôme non renseigné'}
                {profile?.annee_diplome && ` (${profile.annee_diplome})`}
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="bg-white/10 rounded-lg px-4 py-3 text-center">
              <p className="text-3xl font-bold">{stagiaires.length}</p>
              <p className="text-sm text-white/80">Stagiaires</p>
            </div>
          </div>
        </div>
      </div>

      {/* Zen Button */}
      <div className="flex justify-end">
        <ZenButton />
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <a href="/presence" className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-[#8DC63F] hover:shadow-md transition-shadow" data-testid="action-presence">
          <div className="flex items-center space-x-4">
            <div className="bg-[#8DC63F]/10 p-3 rounded-lg">
              <Users className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div>
              <p className="text-lg font-semibold text-[#0F172A]">Présence Stagiaire</p>
              <p className="text-sm text-slate-600">Marquer présent/absent</p>
            </div>
          </div>
        </a>
        <a href="/competences" className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-blue-500 hover:shadow-md transition-shadow" data-testid="action-competences">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-500/10 p-3 rounded-lg">
              <Award className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-lg font-semibold text-[#0F172A]">Evaluer les compétences</p>
              <p className="text-sm text-slate-600">Évaluer le stagiaire</p>
            </div>
          </div>
        </a>
        <AutonomieActionCard linkTo="/recapitulatif-autonomies" />
        <NotificationWidget />
      </div>

      {/* Mes Stagiaires */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <Users className="h-6 w-6 text-[#8DC63F]" />
          <h2 className="text-2xl font-display font-semibold text-[#0F172A]">Mes Stagiaires</h2>
          <span className="text-sm text-slate-400">Cliquez pour voir détails, grille, EPMSP et compétences</span>
        </div>
        {stagiaires.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucun stagiaire attribué</p>
            <p className="text-sm text-slate-400 mt-1">Le centre vous attribuera des stagiaires</p>
          </div>
        ) : (
          <div className="space-y-3">
            {stagiaires.map((stagiaire, index) => (
              <StagiaireAccordionCardTuteur
                key={stagiaire.user_id}
                stagiaire={stagiaire}
                index={index}
                convention={convStageList.find(c => c.stagiaire_id === stagiaire.user_id)}
                grilleStructure={grilleStructure}
                onSignConvention={handleOpenSignDialog}
                onViewConvention={handleViewConv}
                onDownloadConvention={handleDownloadConv}
              />
            ))}
          </div>
        )}
      </div>

      {/* Convention Signature Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent className="max-w-lg" data-testid="tuteur-sign-conv-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="h-5 w-5 text-orange-500" />
              Signature - Convention de Mise en Situation Pédagogique
            </DialogTitle>
            <DialogDescription>
              Convention pour {selectedConv?.stagiaire_prenom} {selectedConv?.stagiaire_nom}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {profile?.signature && (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <input type="checkbox" id="useProfileSig" checked={useProfileSig} onChange={(e) => setUseProfileSig(e.target.checked)} className="rounded" />
                <label htmlFor="useProfileSig" className="text-sm text-green-800 cursor-pointer">Utiliser ma signature enregistrée</label>
              </div>
            )}
            {useProfileSig && profile?.signature ? (
              <div className="border-2 border-green-300 rounded-lg p-4 bg-white text-center">
                <img src={profile.signature} alt="Signature" className="max-h-32 mx-auto" />
                <p className="text-xs text-green-600 mt-2">Signature de votre profil</p>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium mb-2 block">Dessinez votre signature :</label>
                <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white">
                  <SignatureCanvas ref={signRef} canvasProps={{ className: 'w-full h-40 bg-white', style: { touchAction: 'none' } }} penColor="#333" />
                </div>
                <div className="flex justify-between mt-1">
                  <p className="text-xs text-slate-400">Utilisez votre doigt ou stylet</p>
                  <button onClick={() => signRef.current?.clear()} className="text-xs text-slate-500 hover:text-red-500">Effacer</button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowSignDialog(false); setUseProfileSig(false); }}>Annuler</Button>
            <Button onClick={handleSignConvStage} className="bg-orange-500 hover:bg-orange-600" data-testid="tuteur-confirm-sign-btn">
              <CheckCircle className="h-4 w-4 mr-2" /> Valider et signer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bravo Diplome Dialog */}
      <Dialog open={showDiplomeBravo} onOpenChange={dismissDiplomeBravo}>
        <DialogContent className="sm:max-w-lg text-center">
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg animate-bounce">
              <Award className="h-10 w-10 text-white" />
            </div>
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-yellow-600 bg-clip-text text-transparent">
                Bravo !
              </DialogTitle>
              <DialogDescription className="text-base text-[#0F172A] font-medium">
                Votre stagiaire {diplomeStagiaireNom} a reçu un avis favorable pour l'obtention du diplôme.
              </DialogDescription>
            </DialogHeader>
            <p className="text-base font-semibold text-[#8DC63F]">Merci pour votre engagement et votre accompagnement !</p>
          </div>
          <DialogFooter className="justify-center">
            <Button onClick={dismissDiplomeBravo} className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white px-8" data-testid="dismiss-diplome-bravo-btn">
              Merci !
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardTuteur;
